import React, { Suspense, useEffect, useState } from 'react';
import {
  Box,
  Select,
  TextFieldSize,
  TextFieldAppearance,
  Text,
  Icon,
  IconSize,
  Button,
  ButtonSize,
  useSideSheetState,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import { filterDropdownList } from './form-state';
import {
  AvatarRegularStyled,
  IconLayoutContainerStyled,
  ExpiredRodRequestIconStyled,
  RodRequestTableStyled,
  RodRequestTableRowStyled,
  RodRequestCountStyled,
  RodRequestListStyled,
} from './styled';
import { Trans } from '@lingui/macro';
import { find, get, map, sumBy } from 'lodash';
import {
  useName,
  ErrorBoundary,
  SuspenseLoader,
  useAppUser,
  useModulePermission,
  CacheKeys,
} from '@bechtel/client-common';
import { EditMultipleRodRequest } from '@bechtel/client-rod-room';
import { useCrewProgressStatsQuery } from '~/hooks/use-crew-progress-stats';
import {
  CrewMember,
  CrewProgressStats,
  CrewProgressStatsFilterByTerm,
  FilterOperator,
  PersonaType,
} from '@bechtel/types';
import { ReactComponent as NoDataIcon } from '@bechtel/client-assets/dist/images/empty-state_user-detail.svg';
import { AssignedWeldSidesheet } from '@bechtel/client-weld-common';

const FilterListItem = ({ item }: any) => {
  return (
    <Text size="400" color="grey.1000">
      {item.label}
    </Text>
  );
};

export const RodRequestList = () => {
  const { canViewRodRoomModule } = useModulePermission();
  const { getFullName } = useName();
  const { user } = useAppUser();
  const [selectedWelder, setSelectedWelder] = useState<CrewMember | undefined>(undefined);
  const [crewMemberDetails, setCrewMemberDetails] = useState<CrewMember>();
  const {
    visible: visibleEditRodRequest,
    open: openEditRodRequestSideSheet,
    close: closeEditRodRequestSideSheet,
  } = useSideSheetState(false);
  const [loadCrewStats, { loading, data, error, refetch }] = useCrewProgressStatsQuery(true);
  const dashboardSelectedUser: CrewMember = JSON.parse(sessionStorage?.getItem(CacheKeys.DashboardSelectedUser));
  const loggedUserPersona = dashboardSelectedUser?.persona ? dashboardSelectedUser?.persona : user?.crewMember?.persona;
  const loggedUserId = dashboardSelectedUser?.id ? dashboardSelectedUser?.id : user?.crewMember?.id;
  useEffect(() => {
    if (!canViewRodRoomModule || !loggedUserId) return;
    loadCrewStats({
      variables: {
        input: {
          complexFilter: {
            and: [
              {
                term: CrewProgressStatsFilterByTerm.CREW_MEMBER_ID,
                operator: FilterOperator.EQUALS,
                value: loggedUserId,
              },
              {
                term: CrewProgressStatsFilterByTerm.PERSONA_TYPE,
                operator: FilterOperator.EQUALS,
                value: loggedUserPersona,
              },
            ],
          },
        },
        paging: {
          limit: 30,
          cursor: '1',
        },
      },
    });
  }, [canViewRodRoomModule, loggedUserId]);

  const activeRodRequests: CrewProgressStats[] = get(data, 'crewProgressStats.items', []);
  const totalRodRequestsCount = sumBy(activeRodRequests, (obj) => obj?.numRodRequests || 0);
  const crewMemberRodRequestCount = () => {
    const filteredRequest: CrewProgressStats | undefined = find(
      activeRodRequests,
      (activeRodRequest) => activeRodRequest?.crewMember?.id === crewMemberDetails?.id
    );
    return filteredRequest?.numRodRequests;
  };

  const onCloseSidesheet = () => {
    setSelectedWelder(undefined);
  };

  return (
    <RodRequestListStyled disable={!canViewRodRoomModule}>
      <Box px={{ sm: '450' }} py={{ sm: '450' }} justifyContent={{ sm: 'space-between' }}>
        <Box alignItems={{ sm: 'center' }}>
          <Text color="grey.1000" weight="600" family="graphik" data-testid="active-rod-request-title">
            <Trans>Active Rod Requests</Trans>({totalRodRequestsCount})
          </Text>
        </Box>
        <Box style={{ minWidth: '175px' }} data-testid="active-rod-request-day-select">
          <Select
            size={TextFieldSize.SMALL}
            appearance={TextFieldAppearance.NORMAL}
            items={filterDropdownList}
            itemComponent={FilterListItem}
            selectedComponent={FilterListItem}
            initialSelectedItem={filterDropdownList[0]}
            onSelectedItemChange={(changes: any) => {
              // TODO: Filter implementation will be done, once creProgressStats query is updated by BE Team.
            }}
            disabled={true}
          />
        </Box>
      </Box>
      {loading && (
        <Box p={{ sm: '500' }} flexGrow={{ sm: '1' }} justifyContent={{ sm: 'center' }}>
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && !error && activeRodRequests?.length === 0 && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          style={{ minHeight: '240px' }}
          flexGrow={{ sm: '1' }}
        >
          <NoDataIcon />
          <Box mt={{ sm: '600' }} px={{ sm: '700' }} flexShrink={{ sm: '0' }} justifyContent={{ sm: 'center' }}>
            <Text size="400" family="graphik" color="grey.1000" style={{ textAlign: 'center' }}>
              <Trans>Generate a new Rod Request on your dashboard to see your team’s active status</Trans>
            </Text>
          </Box>
          {/* <Box mt={{ sm: '600' }} flexShrink={{ sm: '0' }} justifyContent={{ sm: 'center' }}>
            <Button size={ButtonSize.SMALL} style={{ width: '140px' }} onClick={openNewRodRequestSideSheet}>
              <ActionContent icon="file-spreadsheet" label={t`Bulk Rod Request`} labelSize="300" />
            </Button>
          </Box> */}
        </Box>
      )}
      {!loading && !error && activeRodRequests?.length > 0 && (
        <>
          <RodRequestTableStyled data-testid="active-rod-request-table-header">
            <RodRequestTableRowStyled>
              <Box>
                <Trans>Welder</Trans>
                <Box ml={{ sm: '200' }}>
                  <Icon fillColor="black" icon="sort-up" size={IconSize.SMALL} />
                </Box>
              </Box>
              <Box justifyContent={{ sm: 'center' }}>
                <Trans>Request(s)</Trans>
                <Box ml={{ sm: '200' }}>
                  <Icon fillColor="black" icon="sort-up" size={IconSize.SMALL} />
                </Box>
              </Box>
              <Box justifyContent={{ sm: 'center' }}>
                <Trans>Edit</Trans>
              </Box>
              <Box justifyContent={{ sm: 'center' }}>
                <Trans>View</Trans>
              </Box>
            </RodRequestTableRowStyled>
          </RodRequestTableStyled>
          <Box
            flexWrap={{ sm: 'wrap' }}
            style={{ overflowX: `hidden`, overflowY: `auto` }}
            data-testid="active-rod-request-table-body"
          >
            {activeRodRequests?.length > 0 &&
              map(activeRodRequests, (stat, index) => {
                return stat?.numRodRequests || 0 > 0 ? (
                  <Box
                    style={{ gridRow: `${index}` }}
                    css={{
                      background: stat.hasExpiredRodRequest ? 'rgba(220,99,80,0.07)' : 'transparent',
                      borderTop: `1px solid white`,
                      width: '100%',
                    }}
                  >
                    <RodRequestTableRowStyled>
                      <Box flexDirection={{ sm: 'row' }}>
                        <Box mr={{ sm: '450' }} css={{ position: 'relative' }}>
                          <AvatarRegularStyled src={stat?.crewMember?.imageUrl} />
                          {stat.hasExpiredRodRequest && <ExpiredRodRequestIconStyled />}
                        </Box>
                        <Box flexDirection={{ sm: 'column' }} justifyContent={{ sm: 'space-around' }}>
                          <Text size="400" color="grey.1000" weight="600" family="graphikCompact">
                            {getFullName(stat?.crewMember?.firstName, stat?.crewMember?.lastName)}
                          </Text>
                          <Text size="250" color="grey.1000">
                            {stat?.crewMember?.badgeNumber}
                          </Text>
                        </Box>
                      </Box>
                      <RodRequestCountStyled>{stat.numRodRequests}</RodRequestCountStyled>
                      <Box
                        justifyContent={{ sm: 'center' }}
                        onClick={() => {
                          setCrewMemberDetails(stat.crewMember);
                          openEditRodRequestSideSheet();
                        }}
                      >
                        <IconLayoutContainerStyled pl={{ sm: '200' }}>
                          <Icon fillColor="blue.800" icon="edit" size={IconSize.MEDIUM} />
                        </IconLayoutContainerStyled>
                      </Box>
                      <Box
                        justifyContent={{ sm: 'center' }}
                        onClick={() => {
                          setSelectedWelder(stat?.crewMember);
                        }}
                      >
                        <IconLayoutContainerStyled>
                          <Icon fillColor="blue.800" icon="list" size={IconSize.MEDIUM} />
                        </IconLayoutContainerStyled>
                      </Box>
                    </RodRequestTableRowStyled>
                  </Box>
                ) : null;
              })}
          </Box>
        </>
      )}
      {error && (
        <Box
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box
            pb={{ sm: '500' }}
            flexDirection={{ sm: 'column' }}
            alignItems={{ sm: 'center' }}
            style={{ textAlign: 'center' }}
          >
            <Text>
              <Trans>There was a problem loading data. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Box>
            <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              Retry
            </Button>
          </Box>
        </Box>
      )}
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleEditRodRequest && (
            <EditMultipleRodRequest
              crewMember={crewMemberDetails}
              numRodRequests={crewMemberRodRequestCount()}
              onClose={closeEditRodRequestSideSheet}
            />
          )}
        </Suspense>
      </ErrorBoundary>
      {/* <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {visibleNewRodRequest && <NewRodRequest onClose={closeNewRodRequestSideSheet} welds={[]} />}
        </Suspense>
      </ErrorBoundary> */}
      {selectedWelder && <AssignedWeldSidesheet welder={selectedWelder} onClose={onCloseSidesheet} />}
    </RodRequestListStyled>
  );
};
